import React, { ChangeEvent } from "react";
import { useState } from "react";
import { responseHero } from "../../../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { MiningMachine } from "../../../../models/miningmachine.model";
import { MiningEnergy } from "../../../../models/miningenergy.model";
import { UserGroups } from "../../../../models/usergroups.model";
import { getUserDepositsData } from "../../../../api/getUserDepositsData";
import { getUsersInGroup } from "../../../../api/getUsersInGroup";

/*
    id: number;
  
    kwh: number;
    price: number;
    
    date: Date;
    miningMachineId: number;
    miningMachine: MiningMachine;
    customDistribution: boolean;
*/

type ModalProps = {
    title: string;
    showModal: boolean;
    closeModal: () => void;
    handleStateChange: () => void;
    miningMachine: MiningMachine | undefined;
    group_id: number | undefined;
    globalHandling: boolean;

    row: MiningEnergy | undefined;
}

type ModifiedPayload = Pick<MiningEnergy,
"id" |
"kwh" |
"price" |
"date" |
"paidby" |
"investmentLeftover" |
"miningMachineId">

const ModifyEnergyCostModal = ({ title, showModal, closeModal, handleStateChange, miningMachine, row, group_id, globalHandling } : ModalProps) => {

    const [members, setMembers] = React.useState<UserGroups[]>([]);
    const [selectedMember, setSelectedMember] = React.useState<number>(-1);

    const [recordData, setRecordData] = useState<ModifiedPayload>({
        id: -1,
        kwh: 0,
        price: 0,
        date: new Date(),
        paidby: false,
        investmentLeftover: false,
        miningMachineId: -1
    });

    const [selectedOption, setSelectedOption] = useState('Human D.C fizette');
    const [selectedLeftOver, setSelectedLeftOver] = useState(false);

    const [originalDateKey, setOriginalDateKey] = useState<string>("");
    // 1) helper: safe formatter without toISOString()
    const toDateInputLocal = (d: Date | string | null | undefined) => {
        if (!d) return "";
        const date = d instanceof Date ? d : new Date(d);
        if (isNaN(date.getTime())) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${dd}`;
    };
    
    // 2) keep a separate string for the input field
    const [dateInput, setDateInput] = useState<string>("");

    // 3) when a row arrives, initialize both recordData.date and dateInput
    React.useEffect(() => {
        if (!row) return;
    
        const rowDate = row.date ? new Date(row.date as unknown as string) : new Date();
    
        setRecordData({
        id: row.id ?? -1,
        kwh: row.kwh ?? 0,
        price: row.price ?? 0,
        date: rowDate,
        paidby: !!row?.paidby,
        investmentLeftover: !!row?.investmentLeftover,
        miningMachineId: row?.miningMachineId
        });
    
        setDateInput(toDateInputLocal(rowDate));       // <-- set the string shown in the input
        setOriginalDateKey(toDateInputLocal(rowDate)); // <-- YYYY-MM-DD key stays string-safe
    
        const user = row?.MiningEnergyPayer?.user?.user_id;
        setSelectedMember(user ? user : -1);
    
        setSelectedOption(row?.paidby ? 'Ügyfelek fizették' : 'Human D.C fizette');
        setSelectedLeftOver(!!row?.investmentLeftover);

        // Itt betöltheted a tagokat
        const fetchMembers = async () => {
            if (!group_id) return;
            try {
            const data = await getUsersInGroup(group_id);
            setMembers(data);
            } catch (err) {
                console.error("Nem sikerült betölteni a csoport tagokat:", err);
            setMembers([]);
            }
        };

        fetchMembers();
    }, [row]);

    const handleInvestmentLeftover = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === "true";
    setSelectedLeftOver(value);
    setRecordData(prev => ({ ...prev, investmentLeftover: value }));
    };

    const handleDistributionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const clientsPaid = event.target.value === "clients";
    setSelectedOption(clientsPaid ? "Ügyfelek fizették" : "Human D.C fizette");
    setRecordData(prev => ({ ...prev, paidby: clientsPaid }));
    };

    // 4) a specialized date change handler
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value; // always a string from the input
        setDateInput(v);          // update what user sees immediately
    
        // Only update the Date in state when it’s a full YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        // Append T00:00:00 to avoid TZ shifts; stays on local calendar day
        setRecordData(prev => ({ ...prev, date: new Date(v + "T00:00:00") }));
        }
    };
    
    // 5) update your number/text handler to ignore 'date'
    const handleChange = (type: keyof ModifiedPayload, e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        setRecordData(prev => {
        let newValue: any = inputValue;
        if (type === "kwh" || type === "price") {
            newValue = inputValue === "" ? 0 : parseFloat(inputValue);
        }
        return { ...prev, [type]: newValue };
        });
    };

    const makeRecord = async () => {
    try {
        toast.info("Kérlek várj még a módosítások végbemennek...")
        await responseHero.put(
        `${process.env.REACT_APP_API_URL}/miningEnergy/${row?.id}`,
        {
            originalDateKey,            // <-- send YYYY-MM-DD
            miningEnergy: {
                ...recordData,
                // send a string so there is no timezone conversion
                date: dateInput || toDateInputLocal(recordData.date),
              },
            group_id,
            globalHandling,
            selectedMember
        }
        );
        closeModal();
        toast.success(`Sikeresen frissítettél ${globalHandling ? 'több' : '1'} energiaköltséget!`);
        handleStateChange();
    } catch (error: any) {
        console.error(JSON.stringify(error?.response?.data?.message ?? error, null, 2));
        closeModal();
        toast.error('Sikertelen adatrögzítés!');
    }
    };

    return (
    <>
        {showModal && (
            <>
            <div className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                <div className="relative w-full my-6 mx-auto max-w-4xl">
                    <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                        <div className="flex items-start justify-between p-5 border-b border-solid border-gray-300 rounded-t">
                            <h3 className="text-xl text-brown font-semibold"><span className="text-dark-blue">BÁNYAGÉP</span> - {title}</h3>
                        </div>
                        <div className="relative p-6 flex-auto">
                            
                            <form>
                            <div className="p-5">

                                <div className="flex flex-col space-y-2">
                                <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="leftover"
                                    value="true"
                                    checked={selectedLeftOver === true}
                                    onChange={handleInvestmentLeftover}
                                    className="text-blue-600 form-radio h-5 w-5"
                                />
                                <span className="ml-2 text-gray-700">
                                    Ez az energiafogyasztás a <span className="text-dark-brown">befizetés részeként</span> került rögzítésre
                                </span>
                                </label>

                                <label className="inline-flex items-center">
                                <input
                                    type="radio"
                                    name="leftover"
                                    value="false"
                                    checked={selectedLeftOver === false}
                                    onChange={handleInvestmentLeftover}
                                    className="text-blue-600 form-radio h-5 w-5"
                                />
                                <span className="ml-2 text-gray-700">
                                    Ez az energiafogyasztás <span className="text-dark-brown">különálló</span>, nem a befizetés része
                                </span>
                                </label>

                                </div>

                                <p className="mt-4 text-blue-500">Kiválasztva: {selectedLeftOver ? 'befizetés részeként számító energiaköltség' : 'különálló energiaköltség, amit a befizetés után rendeztek'}</p>
                            </div>
                                <div className="flex p-2 rounded shadow-md">

                                     <div className="w-1/3 pr-2 text-center justify-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center" htmlFor="crypto_1">
                                        <span className="text-success">{miningMachine?.name} </span> <br/>Rögzítés dátuma
                                        </label>
                                        
                                        <input
                                        type="date"
                                        value={dateInput}
                                        onChange={handleDateChange}
                                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        placeholder="Dátum"
                                        />

                                    </div>

                                    <div className="w-1/3 pr-2 text-center justify-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center" htmlFor="crypto_1">
                                            <span className="text-success">{miningMachine?.name} </span> <br/>Fogyasztás (KW/h)
                                        </label>
                                        <input
                                            value={recordData?.kwh}
                                            onChange={(e) => handleChange('kwh', e)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="name" type="number" placeholder="Fogyasztás (kw/h)"/>
                                    </div>

                                    <div className="w-1/3 pr-2 text-center justify-center items-center">
                                        <label className="block text-sm font-bold mt-5 text-brown text-center justify-center" htmlFor="crypto_1">
                                        <span className="text-success">{miningMachine?.name} </span> <br/>Összeg ($)
                                        </label>
                                        <input
                                            value={recordData?.price}
                                            onChange={(e) => handleChange('price', e)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="name" type="number" placeholder="Összeg ($)"/>
                                    </div>

                                    
                                </div>
                                <div className="p-5">

                                    <label className="block text-sm font-bold mt-2 mb-5 text-brown text-center justify-center" htmlFor="crypto_1">
                                        <span className="text-success">{miningMachine?.name} </span> <br/>Részesedés elosztása
                                    </label>

                                    
                                    <div className="flex flex-col space-y-2">
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                name="options"
                                                value="company"
                                                checked={selectedOption === 'Human D.C fizette'}
                                                onChange={handleDistributionChange}
                                                className="text-blue-600 form-radio h-5 w-5"
                                            />
                                            <span className="ml-2 text-gray-700">Ezt az energiafogyasztást a <span className="text-dark-brown">Human D.C</span> fizette.</span>
                                        </label>
                                        <label className="inline-flex items-center">
                                            <input
                                                type="radio"
                                                name="options"
                                                value="clients"
                                                checked={selectedOption === 'Ügyfelek fizették'}
                                                onChange={handleDistributionChange}
                                                className="text-blue-600 form-radio h-5 w-5"
                                            />
                                            <span className="ml-2 text-gray-700">Ezt az energiafogyasztást az <span className="text-dark-brown">Ügyfelek</span> fizették.</span>
                                        </label>
                                    </div>
                                    <p className="mt-4 text-blue-500">Kiválasztva: {selectedOption}</p>
                                    

                                    <>
                                        <select onChange={(e) => setSelectedMember(
                                                parseInt(e.target.value)
                                            )} value={selectedMember} className="w-full bg-dark-blue text-white px-3 py-2 rounded-lg shadow-md">                
                                            <option value={-1} className="px-2 py-1 text-center">
                                                {selectedOption === 'Ügyfelek fizették' ? 'Mindenkire vonatkozik arányosan' :
                                                'A cég a saját költségét fizette'}
                                            </option>
                                            {members.map((member, index) => (
                                                <option value={member.user_id} className="px-2 py-1 text-center" key={index}>{member.user.name}</option>
                                            ))}
                                        </select>
                                    </>
                                </div>
                                
                            </form>
                        </div>
                        <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                        <button
                            className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1"
                            type="button"
                            onClick={closeModal}
                        >
                            Mégse
                        </button>
                        <button
                            className="text-cream bg-dark-blue font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1"
                            type="button"
                            onClick={makeRecord}
                        >
                            Hozzáad
                        </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-filter backdrop-blur-sm"></div>
            </>
        )}
    </>
  );
};

export default ModifyEnergyCostModal;